import { environment } from 'environments/environment';
import apiService from 'services/ApiService';
import { logger } from 'utils/logger';

// Create a service-specific logger
const log = logger.getLogger('SubscriptionsService');

const baseUrl = environment.services.account.baseUrl;
const accountsUrl = baseUrl + environment.services.account.basePath;
const solutionsBaseUrl = environment.services.solution.baseUrl;
const solutionsUrl = solutionsBaseUrl + environment.services.solution.basePath;


export interface UserSolutionSubscriptionItem {
    id: number;
    user: {
        id: number;
        enabled: boolean;
        username: string;
        privs: any[];
        firstName: string;
        lastName: string;
    };
    tag: string;
    solution: {
        id: number;
        name: string;
    };
    subscription: {
        id: number;
        name: string;
    };
    summarySchedules: SummarySchedule[];
}

export interface UserSolutionSubscriptionResponse {
    count: number;
    data: UserSolutionSubscriptionItem[];
}

export interface Account {
    id: number;
    name: string;
    // Other properties might be included here
}

export interface AccountsResponse {
    count: number;
    data: Account[];
}

export interface Solution {
    id: number;
    account: {
        id: number;
    };
    name: string;
    description: string;
    notes: string;
    enabled: boolean;
    active: boolean;
    locked: boolean;
    type: string;
    efgType: string;
    createDate: string; // ISO date string
    updateDate: string; // ISO date string
    createdBy: number;
    updatedBy: number;
    config: Config;
}

export interface SolutionsResponse {
    count: number;
    data: Solution[];
}
export interface Config {
    module: string;
    feedback: FeedbackConfig;
    displayConfig: DisplayConfig;
}
export interface FeedbackConfig {
    valediction: {
        type: string;
        useMenuLevelValediction: boolean;
        useAdvancedValedictionLogic: boolean;
    };
    restrictions: any[]; // Replace `any` if restrictions structure is known
    notifications: {
        allowSummaryNotifications: boolean;
        allowFeedbackNotifications: boolean;
    };
}
export interface DisplayConfig {
    palette: PaletteConfig;
    branding: BrandingConfig;
}

export interface PaletteConfig {
    nps: PaletteColors;
    ratings: PaletteColors;
    hxrating: PaletteColors;
    multiselect: ResponseColors;
    singleselect: ResponseColors;
}

export interface PaletteColors {
    default: string;
    passive?: string;
    promoter?: string;
    detractor?: string;
    neutral?: string;
    negative?: string;
    positive?: string;
}

export interface ResponseColors {
    default: string;
    response: string;
}

export interface BrandingConfig {
    menu: MenuBranding;
    title: TitleBranding;
}

export interface MenuBranding {
    menuPrompt: string;
    instructions: Instruction[];
    randomizeItems: boolean;
    menuPrompt_i18n: {
        [locale: string]: string;
    };
    showFeedbackHistory: boolean;
    menuSearchPlaceholder: string;
    searchOnlyLandingPage: boolean;
    randomizeFoldersInLevel1: boolean;
    menuSearchPlaceholder_i18n: {
        [locale: string]: string;
    };
    randomizeFoldersInLevel2Plus: boolean;
}

export interface Instruction {
    type: string;
    imageUrl: string;
    businessKey: string;
    instructionHtml: string;
    instructionHtml_i18n: {
        [locale: string]: string;
    };
}

export interface TitleBranding {
    heading1_i18n: {
        [locale: string]: string;
    };
    heading2_i18n: {
        [locale: string]: string;
    };
}

export interface Subscription {
    id: number;
    name: string;
    // Other subscription properties
}

export interface SubscriptionsResponse {
    count: number;
    data: Subscription[];
}
export interface SummaryScheduleResponse {
    count: number;
    data: SummarySchedule[];
}
export interface SummarySchedule {
    id: number;
    template: {
        id: number;
        version: number;
    };
    key: string;
    name: string;
    subject: string;
    startDate: string; // ISO 8601 string
    endDate: string;   // ISO 8601 string
    period: number;
    state: string;
    enabled: boolean;
    createDate: string; // ISO 8601 string
    createdBy: number;
    updateDate: string; // ISO 8601 string
    updatedBy: number;
    summaryScheduleId?: number;
}

class SubscriptionsService {
    /**
     * Get all subscriptions for a user
     */
    async getUserSubscriptions(userId: number, accountId: number): Promise<UserSolutionSubscriptionResponse> {
        try {
            const apiUrl = `${accountsUrl}/users/${userId}/subscriptions/?sort=name&page=0&size=49999999&accountId=${accountId}&include=SOLUTION_NAME`;
            log.log('Fetching user subscriptions from API:', apiUrl);

            const response = await apiService.makeRequest('get', apiUrl, undefined, false);
            return response.data;
        } catch (error) {
            log.error(`Error fetching subscriptions for user ${userId}:`, error);
            throw error;
        }
    }

    /**
     * Delete a subscription from a user
     */
    async removeSubscriptionFromUser(userId: number, subscriptionId: number): Promise<any> {
        try {
            const apiUrl = `${accountsUrl}/users/${userId}/subscriptions/${subscriptionId}`;
            log.log('Removing subscription from user:', apiUrl);

            const response = await apiService.makeRequest('delete', apiUrl);

            // Invalidate cache for users subscriptions
            apiService.invalidateCacheByPattern(`/users/${userId}/subscriptions`);

            return response;
        } catch (error) {
            log.error(`Error removing subscription ${subscriptionId} from user ${userId}:`, error);
            throw error;
        }
    }

    /**
     * Get all accounts
     */
    async getAccounts(): Promise<AccountsResponse> {
        try {
            const apiUrl = `${accountsUrl}/accounts/?sort=name&direction=asc&size=49999999`;
            log.log('Fetching accounts from API:', apiUrl);

            const response = await apiService.makeRequest('get', apiUrl);
            return response;
        } catch (error) {
            log.error('Error fetching accounts:', error);
            throw error;
        }
    }

    /**
     * Get solutions for an account
     */
    async getAccountSolutions(accountId: number): Promise<SolutionsResponse> {
        try {
            const apiUrl = `${solutionsUrl}/solutions/?accountId=${accountId}&sort=name&direction=asc&size=49999999`;
            log.log('Fetching solutions for account from API:', apiUrl);

            const response = await apiService.makeRequest('get', apiUrl);
            return response;
        } catch (error) {
            log.error(`Error fetching solutions for account ${accountId}:`, error);
            throw error;
        }
    }
    
    /**
     * Get solution details by ID
     */
    async getSolutionById(solutionId: number): Promise<Solution> {
        try {
            const apiUrl = `${solutionsUrl}/solutions/${solutionId}`;
            log.log('Fetching solution details from API:', apiUrl);

            const response = await apiService.makeRequest('get', apiUrl);
            return response.data;
        } catch (error) {
            log.error(`Error fetching solution details for solution ${solutionId}:`, error);
            throw error;
        }
    }

    /**
     * Get subscriptions for an account - used for subscription dropdown
     */
    async getAccountSubscriptions(accountId: number): Promise<SubscriptionsResponse> {
        try {
            const apiUrl = `${accountsUrl}/subscriptions/?accountId=${accountId}&global=true&sort=name&direction=asc&size=49999999`;
            log.log('Fetching subscriptions for account from API:', apiUrl);

            const response = await apiService.makeRequest('get', apiUrl, undefined, false);
            log.log('Received account subscriptions data:', response);
            return response.data;
        } catch (error) {
            log.error(`Error fetching subscriptions for account ${accountId}:`, error);
            throw error;
        }
    }

    /**
     * Get summary schedules for a solution
     */
    async getSolutionSummarySchedules(solutionId: number): Promise<SummarySchedule[]> {
        try {
            const apiUrl = `${solutionsUrl}/solutions/${solutionId}/notificationsConfig/summarySchedules/`;
            log.log('Fetching summary schedules from API:', apiUrl);

            const response = await apiService.makeRequest('get', apiUrl);
            return response.data;
        } catch (error) {
            log.error(`Error fetching summary schedules for solution ${solutionId}:`, error);
            // Return empty array on error instead of throwing
            return [];
        }
    }

    /**
     * Add a subscription to a user
     */
    async addSubscriptionToUser(userId: number, data: {
        accountId: number;
        solutionId: number;
        subscriptionId: number;
        summaryScheduleIds?: number[];
    }): Promise<any> {
        try {
            const apiUrl = `${accountsUrl}/users/${userId}/subscriptions`;
            log.log('Adding subscription to user:', apiUrl);
            log.log('Request data:', data);

            const response = await apiService.makeRequest('post', apiUrl, data);

            // Invalidate cache for users subscriptions
            apiService.invalidateCacheByPattern(`/users/${userId}/subscriptions`);

            return response.data;
        } catch (error) {
            log.error(`Error adding subscription for user ${userId}:`, error);
            throw error;
        }
    }

    /**
     * Update a user subscription
     */
    async updateUserSubscription(userId: number, subscriptionId: number, data: {
        accountId: number;
        solutionId: number;
        subscriptionId: number;
        summaryScheduleIds?: number[];
    }): Promise<any> {
        try {
            const apiUrl = `${accountsUrl}/users/${userId}/subscriptions/${subscriptionId}`;
            log.log('Updating user subscription:', apiUrl);
            log.log('Request data:', data);

            const response = await apiService.makeRequest('put', apiUrl, data);

            // Invalidate cache for users subscriptions
            apiService.invalidateCacheByPattern(`/users/${userId}/subscriptions`);

            return response.data;
        } catch (error) {
            log.error(`Error updating subscription ${subscriptionId} for user ${userId}:`, error);
            throw error;
        }
    }
}

export const subscriptionsService = new SubscriptionsService();
export default subscriptionsService;